import { TRPCErrorBoundary } from '@client/components/_core/error-handling/TRPCErrorBoundary';
import { PageController } from '@client/components/_core/layout/PageController';
import { TableToolbar } from '@client/components/_core/table/TableToolbar';
import { Button } from '@client/components/_ui/data-entry/ButtonModern';
import { errorMessage } from '@client/components/_ui/feedback/error-message/ErrorMessage';
import { message } from '@client/components/_ui/feedback/message';
import { Space } from '@client/components/_ui/layout/Space';
import { ReadOnlyBanner } from '@client/components/admin/ReadOnlyBanner';
import { ImportVulnerabilityExampleModal } from '@client/components/admin/vulnerability-example/ImportVulnerabilityExampleModal';
import { useAdminModulePermissions } from '@client/hooks/admin/useAdminModulePermissions';
import { useTrpc } from '@client/hooks/api/useTrpc';
import { useNavigate } from '@client/hooks/navigation/useNavigate';
import { usePathState } from '@client/hooks/navigation/usePathState';
import { createColumnHelper, PrismaTable, usePrismaTable } from '@client/hooks/table-v2';
import { useFormatter } from '@client/hooks/utils/useFormatter';
import type { RouterInputs, RouterOutputs } from '@client/trpc/client';
import { DEFAULT_PAGE_SIZE } from '@shared/constants';
import { createFileRoute, Outlet } from '@tanstack/react-router';
import { Empty, Tag, Tooltip } from 'antd';
import { Download, Plus, ShieldAlert } from 'lucide-react';
import { useState } from 'react';
import { VulnerabilityExampleFilters } from './VulnerabilityExampleFilters';

const EMPTY_AVAILABLE_TAGS: string[] = [];

export const Route = createFileRoute('/admin-console/platform-content/ai-training/vulnerability-example')({
    staticData: {
        breadcrumb: { title: 'Vulnerability examples' },
        ui: {
            icon: ShieldAlert,
            title: 'Vulnerability examples management',
            subtitle: 'Curated few-shot examples for vulnerability detection',
            menuDescription: 'Manage vulnerability training examples',
        },
    },
    component: VulnerabilityExamples,
});

type QueryParams = RouterInputs['admin']['vulnerabilityExample']['listVulnerabilityExample'];
type Data = RouterOutputs['admin']['vulnerabilityExample']['listVulnerabilityExample'];
type Row = NonNullable<Data>['rows'][number];
type OrderBy = NonNullable<QueryParams['orderBy']>;

function hasActiveFilters(where: QueryParams['where']): boolean {
    return Boolean(where && Object.keys(where).length > 0);
}

function VulnerabilityExamples() {
    const { trpc } = useTrpc();
    const navigate = useNavigate();
    const { formatDate } = useFormatter();
    const { isReadOnly } = useAdminModulePermissions('ai');
    const columnHelper = createColumnHelper<Row, OrderBy>();
    const [importModalOpen, setImportModalOpen] = useState(false);
    const [queryParams, setQueryParams] = usePathState<QueryParams>('query-params', {
        limit: DEFAULT_PAGE_SIZE,
        offset: 0,
    });

    const { data, isLoading, refetch, error } =
        trpc.admin.vulnerabilityExample.listVulnerabilityExample.useQuery(queryParams);
    const availableTags = data?.availableTags ?? EMPTY_AVAILABLE_TAGS;
    const hasFilters = hasActiveFilters(queryParams.where);

    const { mutate: deleteExample } = trpc.admin.vulnerabilityExample.deleteVulnerabilityExample.useMutation({
        onSuccess: () => {
            message.success('Example deleted successfully');
            void refetch();
        },
        onError: (err) => errorMessage.show(err),
    });

    const columns = [
        columnHelper.display({
            id: 'vulnerabilityType',
            header: 'Type',
            cell: ({ row }) => (
                <Space size={4} wrap>
                    <Tag variant="outlined" color="blue" className="font-mono text-xs">
                        {row.vulnerabilityType.label}
                    </Tag>
                    {row.vulnerabilityType.isSensitive ? (
                        <Tag variant="outlined" color="red" className="text-xs">
                            sensitive
                        </Tag>
                    ) : null}
                </Space>
            ),
            width: 240,
        }),
        columnHelper.accessor('actor', {
            id: 'actor',
            header: 'Actor',
            cell: ({ value }) =>
                value ? (
                    <span className="font-mono text-subtle-foreground text-xs">{value}</span>
                ) : (
                    <span className="text-sm text-subtle-foreground">—</span>
                ),
            width: 180,
        }),
        columnHelper.accessor('bodyExample', {
            id: 'bodyExample',
            header: 'Body',
            cell: ({ value }) => (
                <div className="max-w-[420px] truncate text-subtle-foreground" title={value}>
                    {value}
                </div>
            ),
            width: 420,
        }),
        columnHelper.accessor('isCounterExample', {
            id: 'isCounterExample',
            header: 'Counter?',
            sortable: 'isCounterExample',
            cell: ({ value }) =>
                value ? (
                    <Tag variant="outlined" color="orange" className="text-xs">
                        Counter
                    </Tag>
                ) : (
                    <span className="text-sm text-subtle-foreground">—</span>
                ),
            width: 100,
        }),
        columnHelper.accessor('tags', {
            id: 'tags',
            header: 'Tags',
            cell: ({ value }) => {
                if (!value || value.length === 0) {
                    return <span className="text-sm text-subtle-foreground">No tags</span>;
                }
                return (
                    <Space size={4} wrap>
                        {value.slice(0, 3).map((tag: string) => (
                            <Tag variant="outlined" key={tag} color="geekblue" className="text-xs">
                                {tag}
                            </Tag>
                        ))}
                        {value.length > 3 && (
                            <Tooltip title={value.slice(3).join(', ')}>
                                <Tag variant="outlined" className="cursor-help text-xs">
                                    +{value.length - 3}
                                </Tag>
                            </Tooltip>
                        )}
                    </Space>
                );
            },
            width: 220,
        }),
        columnHelper.accessor('createdAt', {
            id: 'createdAt',
            header: 'Created',
            sortable: 'createdAt',
            cell: ({ value }) => formatDate(value),
            width: 120,
        }),
        columnHelper.accessor('updatedAt', {
            id: 'updatedAt',
            header: 'Updated',
            sortable: 'updatedAt',
            cell: ({ value }) => formatDate(value),
            width: 120,
        }),
        columnHelper.actions({
            id: 'actions',
            cell: ({ row }) => (
                <Space size="small">
                    <Button.View.Link
                        to="/admin-console/platform-content/ai-training/vulnerability-example/view/$id"
                        params={{ id: row.id }}
                    />
                    {!isReadOnly && (
                        <>
                            <Button.Edit.Link
                                to="/admin-console/platform-content/ai-training/vulnerability-example/edit/$id"
                                params={{ id: row.id }}
                            />
                            <Button.Delete
                                confirmTitle="Delete vulnerability example"
                                confirmDescription="Are you sure you want to delete this example? This action cannot be undone."
                                onConfirm={(onSuccess) => {
                                    deleteExample(
                                        { id: row.id },
                                        {
                                            onSuccess: () => onSuccess(),
                                        },
                                    );
                                }}
                            />
                        </>
                    )}
                </Space>
            ),
            fixed: 'right',
            align: 'center',
        }),
    ];

    const tableProps = usePrismaTable({
        data: data?.rows || [],
        total: data?.total || 0,
        loading: isLoading,
        columns,
        rowKey: 'id',
        queryParams,
        onParamsChange: (params) => setQueryParams(params),
        pagination: { position: 'both' },
        columnVisibility: {
            enabled: true,
            persistKey: 'vulnerability-example-columns',
            enableReordering: true,
        },
        refreshable: { enabled: true, onRefresh: () => refetch() },
        showSorterTooltip: false,
    });

    const handleFilterChange = (where: Partial<QueryParams['where']>) => {
        setQueryParams((prev) => ({ ...prev, where, offset: 0 }));
    };

    return (
        <PageController route={Route}>
            <TRPCErrorBoundary error={error}>
                <ReadOnlyBanner module="ai" />
                <TableToolbar
                    filters={
                        <VulnerabilityExampleFilters availableTags={availableTags} onChange={handleFilterChange} />
                    }
                    variant="enhanced"
                    actions={
                        !isReadOnly ? (
                            <Space>
                                <Button type="secondary" icon={<Download />} onClick={() => setImportModalOpen(true)}>
                                    Import CSV
                                </Button>
                                <Button
                                    type="primary"
                                    icon={<Plus />}
                                    onClick={() =>
                                        navigate({
                                            to: '/admin-console/platform-content/ai-training/vulnerability-example/create',
                                        })
                                    }
                                >
                                    Add example
                                </Button>
                            </Space>
                        ) : undefined
                    }
                />

                {data?.rows.length === 0 && !isLoading ? (
                    <Empty
                        description={
                            <Space orientation="vertical" size="small">
                                <span>No vulnerability examples found</span>
                                <span className="text-sm text-subtle-foreground">
                                    {hasFilters
                                        ? 'Try adjusting your filters or search criteria'
                                        : 'Import a CSV or create your first example to get started'}
                                </span>
                            </Space>
                        }
                        className="py-12"
                    >
                        {!hasFilters && (
                            <Button
                                type="primary"
                                icon={<Plus />}
                                onClick={() =>
                                    navigate({
                                        to: '/admin-console/platform-content/ai-training/vulnerability-example/create',
                                    })
                                }
                                disabled={isReadOnly}
                            >
                                Create example
                            </Button>
                        )}
                    </Empty>
                ) : (
                    <PrismaTable {...tableProps} />
                )}

                <ImportVulnerabilityExampleModal
                    open={importModalOpen}
                    onClose={() => setImportModalOpen(false)}
                    onSuccess={() => void refetch()}
                />

                <Outlet />
            </TRPCErrorBoundary>
        </PageController>
    );
}
